import styled from 'styled-components'
import { useEffect, useMemo, useRef } from 'react'
import { Wave } from '../../../components/wave/Wave'
import { theme } from '../../../theme/theme'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import {
  useGetHealthPlanCardsQuery,
  useGetHealthPlanSummaryQuery,
  useGetCarePlanQuery,
  useGetUserHealthPlanOutcomesQuery,
  useGetCheckListQuery,
} from '../../../api/ester-b2b-api/private/healthPlan/healthPlanQueries'
import { HealthCareOptions } from '../../../components/healthPlan/HealthCareOptions'
import { BodySmall, BodySmallMedium } from '../../../components/styles/Typography'
import { useTranslation } from 'react-i18next'
import { useSelectScreeningStatus, useSelectUserLanguage, useSelectUserMarket } from '../../../state/selectors'
import { Outcomes } from '../../../components/healthPlan/Outcomes'
import { HealthPlanCardSection } from '../../../components/healthPlan/healthPlanCard/HealthPlanCardSection'
import { CheckList } from '../../../components/healthPlan/CheckList'
import { Info } from '../../../components/icons'
import {
  HealthPlanSummary,
  CarePlanFormatted,
  CheckListResponse,
  HealthPlanOutcomeResponse,
  CombinedHealthCard,
} from '../../../api/ester-b2b-api/private/healthPlan/types'
import { useGetEntryById } from '../../../api/contentful/contentfulQueries'
import { TextWithImageCard } from '../../../components/TextWithImageCard'
import { isITextWithImageBlock, ScreeningStatus, TrackingEvents } from '@estercare/ester-shared'
import { isSwedishMarket } from '../../../utils/market'
import { useEmitEvent } from '../../../hooks/analytics/useEmitEvent'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { SymptomProgress } from '../../../components/healthPlan/SymptomProgress'
import { SummaryCard } from '../../../components/healthPlan/SummaryCard'

export const HealthPlanV2 = () => {
  const { t } = useTranslation()
  const userLanguage = useSelectUserLanguage()
  const userMarket = useSelectUserMarket()
  const screeningStatus = useSelectScreeningStatus()

  const isFollowUpHandled = screeningStatus === ScreeningStatus.FOLLOW_UP_HANDLED
  const isFollowUpCompleted =
    screeningStatus === ScreeningStatus.FOLLOW_UP_PENDING || screeningStatus === ScreeningStatus.FOLLOW_UP_HANDLED

  const {
    data: healthPlanSummaryV2,
    isLoading: isHealthPlanSummaryLoading,
    isFetching: isHealthPlanSummaryFetching,
    isError: isHealthPlanSummaryError,
  } = useGetHealthPlanSummaryQuery()
  const { data: healthPlanCardsData, isFetching: isHealthPlanCardsFetching } = useGetHealthPlanCardsQuery()
  const { data: carePlanData, isFetching: isCarePlanFetching } = useGetCarePlanQuery()
  const { data: outcomes, isFetching: isOutcomesFetching } = useGetUserHealthPlanOutcomesQuery()
  const { data: checkList, isFetching: isCheckListFetching } = useGetCheckListQuery()
  const emitEvent = useEmitEvent()
  const hasEmittedRef = useRef(false)

  useEffect(() => {
    if (!isHealthPlanSummaryLoading && healthPlanSummaryV2 && !hasEmittedRef.current) {
      hasEmittedRef.current = true
      emitEvent({ eventType: TrackingEvents.OPEN_HEALTH_PLAN })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHealthPlanSummaryLoading, healthPlanSummaryV2])

  const isContentFetching =
    isHealthPlanSummaryFetching ||
    isHealthPlanCardsFetching ||
    isCarePlanFetching ||
    isOutcomesFetching ||
    isCheckListFetching

  const hasLanguageMismatch = useMemo(() => {
    if (isContentFetching) return false

    const healthCardMissingLanguage =
      healthPlanCardsData && healthPlanCardsData.some((card) => card.language && card.language !== userLanguage)

    const contentToCheck: (HealthPlanSummary | CarePlanFormatted | CheckListResponse | HealthPlanOutcomeResponse)[] = [
      healthPlanSummaryV2,
      carePlanData,
      checkList,
      outcomes,
    ].filter((item) => item !== undefined && item !== null)

    return contentToCheck.some((item) => item.language && item.language !== userLanguage) || healthCardMissingLanguage
  }, [carePlanData, checkList, healthPlanCardsData, healthPlanSummaryV2, isContentFetching, outcomes, userLanguage])

  const combinedCards: CombinedHealthCard[] = useMemo(() => {
    const baseCards = healthPlanCardsData || []

    if (!carePlanData) {
      return baseCards
    }

    if (carePlanData.careLevel === 1) {
      return [...baseCards, carePlanData]
    }

    if (carePlanData.careLevel === 2 || carePlanData.careLevel === 3) {
      return [carePlanData, ...baseCards]
    }

    return baseCards
  }, [healthPlanCardsData, carePlanData])

  const startConversationBlock = useGetEntryById('25O3ItN5ohW7W1nxmKrRK7').data
  const convoBlock = isITextWithImageBlock(startConversationBlock) && (
    <TextWithImageCard {...startConversationBlock.fields} />
  )

  if (isHealthPlanSummaryLoading) {
    return (
      <ComponentLayout>
        <LoadingSpinner color={theme.color.plum} size={60} />
      </ComponentLayout>
    )
  }

  return (
    <>
      {hasLanguageMismatch && (
        <ComponentLayout paddingBottom={0}>
          <ResultContainer>
            <Info size={32} color={theme.color.yellow} />
            <TextWrapper>
              <BodySmallMedium>{t('employee.yourHealth.translationsMissing1')}</BodySmallMedium>
              <BodySmall>{t('employee.yourHealth.translationsMissing2')}</BodySmall>
            </TextWrapper>
          </ResultContainer>
        </ComponentLayout>
      )}
      <SymptomProgress isFollowUpCompleted={isFollowUpCompleted} />
      {!isHealthPlanSummaryError && healthPlanSummaryV2 && (
        <ComponentLayout paddingBottom={0}>
          <SummaryCard content={healthPlanSummaryV2.content} isFollowUpHandled={isFollowUpHandled} />
        </ComponentLayout>
      )}

      <Wave color={theme.color.beigeDark} />
      <HealthPlanContainer>
        <ComponentLayout backgroundColor={theme.color.beigeDark} paddingTop={0} paddingBottom={0}>
          <CheckList checkListItems={checkList?.items} isFollowUpHandled={isFollowUpHandled} />
        </ComponentLayout>
        <ComponentLayout backgroundColor={theme.color.beigeDark} paddingBottom={0}>
          <Outcomes outcomes={outcomes?.outcomes} />
        </ComponentLayout>
        {combinedCards.length > 0 && (
          <ComponentLayout backgroundColor={theme.color.beigeDark} paddingBottom={0}>
            <HealthPlanCardSection cards={combinedCards} />
          </ComponentLayout>
        )}
        <ComponentLayout backgroundColor={theme.color.beigeDark}>{convoBlock}</ComponentLayout>
        {isSwedishMarket(userMarket) && (
          <ComponentLayout backgroundColor={theme.color.beigeDark} paddingTop={0}>
            <HealthCareOptions />
          </ComponentLayout>
        )}
      </HealthPlanContainer>
    </>
  )
}

const HealthPlanContainer = styled.div`
  width: 100%;
  background-color: ${theme.color.beigeDark};
  padding-bottom: ${theme.spacing.xxlarge}px;
`

const ResultContainer = styled.div`
  background-color: ${theme.color.yellowLight};
  border-radius: ${theme.spacing.small}px;
  padding: ${theme.spacing.medium}px;
  border: 1px solid ${theme.color.yellow};
  display: flex;
  gap: ${theme.spacing.medium}px;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    flex-direction: column;
    align-items: center;
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.tiny}px;
`
